import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { useForm, useFieldArray } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import { GridForm, GridButtonWrapper } from 'components/Ui/styled';
import List from '@mui/material/List';
import { useSelector, useDispatch } from 'react-redux';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useDebouncedCallback } from 'use-debounce';
import {
  getDialogHeading,
  useNotification,
  renderSaveCancelButtons,
  showBackdropLoader,
  fieldValidation,
  getSnackBar,
  refinePayload,
} from 'utils/commonUtils';
import { requiredLimit, characterLimit } from 'constants';
import { resetTrackingForm } from 'layouts/studentTracking/store/trackingSlice/trackingSlice';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import MultiEditFormTextField from 'components/Common/Forms/MultiEditFormTextField';
import { PaperPropsCheck, requiredField } from 'constants';
import {
  BoxWrapper,
  PButton,
  ButtonWrapper,
  ButtonListItem,
  IconDragIndicator,
} from 'components/Ui/styled';
import {
  postColumn,
  postRenameTracker,
  putReorderTrackerColumns,
  postColumnRename,
} from 'layouts/studentTracking/services/studentTrackerApi';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import {
  trackerSuccess,
  renameTrackerColumnSuccess,
  reorderTrackerColumnSuccess,
} from 'constants';
import { setTrackingForm } from 'layouts/studentTracking/store/trackingSlice/trackingSlice';
import { getTracker } from 'layouts/studentTracking/services/studentTrackerApi';

const schema = yup.object().shape({
  name: fieldValidation(characterLimit),
  fields: yup.array().of(
    yup.object().shape({
      value: yup
        .string()
        .max(100, requiredLimit)
        .trim()
        .required(requiredField),
    })
  ),
});

const TrackerEditDialog = (props) => {
  const dispatch = useDispatch();
  const {
    open,
    setOpen,
    trackerId,
    fetchTrackers,
    isFormLoading,
    setTrackerId,
  } = props;

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const [loadingButton, setLoadingButton] = useState(false);
  const [editableFieldIndex, setEditableFieldIndex] = useState(null);
  const [isDragAndDropped, setIsDragAndDropped] = useState(false);
  const [isNewColumn, setIsNewColumn] = useState(false);
  const [isAPICall, setIsAPICall] = useState(false);

  const trackingForm = useSelector((state) => state?.trackingForm) || {};
  const { name, isNewTracking } = trackingForm || {};

  const editColumns = trackingForm?.columns?.map((col) => ({ value: col }));

  const defaultValue = {
    name: name,
    fields: isNewTracking ? [{ value: '' }] : editColumns,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset, control, formState, setValue, register } =
    useFunction;
  const { errors } = formState;

  const { fields, append, move } = useFieldArray({
    control,
    name: 'fields',
  });

  const lastColumnRef = useRef(null);

  useEffect(() => {
    if (isDragAndDropped) {
      reorderTrackerColumns();
    }
  }, [isDragAndDropped]);

  const reorderTrackerColumns = async () => {
    try {
      const snackBar = getSnackBar(reorderTrackerColumnSuccess);
      setIsAPICall(true);
      const columnOrder = fields?.map((item) => item.value.trim());
      const reorderTrackerColumnsPayload = {
        columnOrder: columnOrder,
      };
      await putReorderTrackerColumns(reorderTrackerColumnsPayload, trackerId);
      const tracker = await getTracker(trackerId);
      dispatch(setTrackingForm(tracker));
      dispatch(displaySnackbar(snackBar));
      fetchTrackers();
      setIsDragAndDropped(false);
      setIsAPICall(false);
    } catch (error) {
      setIsAPICall(false);
      setIsDragAndDropped(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    reset({
      ...defaultValue,
      fields: defaultValue.fields?.length
        ? defaultValue.fields
        : [{ value: '' }],
    });
  }, [name, JSON.stringify(defaultValue.fields)]);

  const handleClose = () => {
    reset(defaultValue);
    dispatch(resetTrackingForm());
    setEditableFieldIndex(null);
    setTrackerId(null);
    setIsDragAndDropped(false);
    setIsNewColumn(false);
    setOpen(false);
  };

  const handleDragDrop = (result) => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
    setIsDragAndDropped(true);
  };

  const handleAddColumn = () => {
    append({ value: '' });
    setIsNewColumn(true);
    setTimeout(() => {
      if (lastColumnRef.current) {
        lastColumnRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const isMaxColumns = fields.length < 10 ? false : true;

  const handleEditClick = (index) => {
    setEditableFieldIndex(index);
  };

  const debouncedRenameColumn = useDebouncedCallback(
    async (original, edited) => {
      try {
        const snackBar = getSnackBar(renameTrackerColumnSuccess);
        setIsAPICall(true);
        if (isNewColumn) {
          const columNamePayload = {
            columName: edited,
          };
          await postColumn(refinePayload(columNamePayload), trackerId);
          setIsNewColumn(false);
        } else {
          const renameColumnPayload = {
            columnName: original,
            newColumnName: edited,
          };
          await postColumnRename(refinePayload(renameColumnPayload), trackerId);
        }
        const tracker = await getTracker(trackerId);
        dispatch(setTrackingForm(tracker));
        dispatch(displaySnackbar(snackBar));
        setEditableFieldIndex(null);
        fetchTrackers();
        setIsAPICall(false);
      } catch (error) {
        setIsDragAndDropped(false);
        handleErrorResponse(error?.response?.data?.message);
        setEditableFieldIndex(null);
        setIsAPICall(false);
      }
    },
    1000
  );

  const handleColumnChange = (index, newValue) => {
    const originalColumnName = fields[index].value.trim();
    if (originalColumnName !== newValue.trim()) {
      debouncedRenameColumn(originalColumnName, newValue.trim());
    }
  };

  const onSubmit = async (data) => {
    const { name } = data;
    const snackBar = getSnackBar(trackerSuccess);

    try {
      setLoadingButton(true);
      if (name !== defaultValue.name) {
        const renameTrackerNamePayload = {
          name,
        };
        await postRenameTracker(
          refinePayload(renameTrackerNamePayload),
          trackerId
        );
      }
      dispatch(displaySnackbar(snackBar));
      setLoadingButton(false);
      setOpen(false);
      fetchTrackers();
      dispatch(resetTrackingForm());
    } catch (error) {
      setLoadingButton(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  return (
    <>
      {showBackdropLoader(isFormLoading)}
      <Dialog open={open} PaperProps={PaperPropsCheck}>
        {getDialogHeading('Edit Tracker', handleClose, 'editTracker')}
        {showBackdropLoader(isAPICall)}
        <BoxWrapper>
          <GridForm container spacing={2}>
            <Grid item xs={12}>
              <FormInputText
                name="name"
                type="text"
                control={control}
                label="Name"
                helperText={errors?.name?.message}
                errors={!!errors.name}
              />
              <ButtonWrapper container>
                <PButton
                  variant="text"
                  onClick={handleAddColumn}
                  disabled={isMaxColumns}
                  data-testid="add-column"
                >
                  Add Column +
                </PButton>
              </ButtonWrapper>
              <DragDropContext onDragEnd={handleDragDrop}>
                <Droppable droppableId="reorderColumns">
                  {(provided) => (
                    <List {...provided.droppableProps} ref={provided.innerRef}>
                      {fields.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided) => (
                            <ListItem
                              ref={(node) => {
                                provided.innerRef(node);
                                if (index === fields.length - 1) {
                                  lastColumnRef.current = node;
                                }
                              }}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <ButtonListItem>
                                <ListItemIcon data-testid="list-item-icon">
                                  <IconDragIndicator fontSize="medium" />
                                </ListItemIcon>
                                <MultiEditFormTextField
                                  label="Column"
                                  fields={fields}
                                  field={item}
                                  index={index}
                                  control={control}
                                  errors={errors}
                                  isEditable={editableFieldIndex === index}
                                  onEditClick={() => handleEditClick(index)}
                                  register={register(`fields[${index}].value`, {
                                    onChange: (e) =>
                                      handleColumnChange(index, e.target.value),
                                  })}
                                />
                              </ButtonListItem>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
          </GridForm>
        </BoxWrapper>
        <GridButtonWrapper container>
          {renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            loadingButton
          )}
        </GridButtonWrapper>
      </Dialog>
      <NotificationPopup />
    </>
  );
};

TrackerEditDialog.propTypes = {
  open: PropTypes.bool,
  trackerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFormLoading: PropTypes.bool,
  setTrackerId: PropTypes.func,
  fetchTrackers: PropTypes.func,
};

TrackerEditDialog.defaultProps = {
  open: false,
  trackerId: null,
  isFormLoading: false,
  setTrackerId: () => {},
  fetchTrackers: () => {},
};

export default TrackerEditDialog;
